import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import Swal from 'sweetalert2'
import { format } from "date-fns";
import withReactContent from 'sweetalert2-react-content'

import {
    Chip,
    Button,
    Typography,
    Card,
    Checkbox,
    Input,
    Avatar,
    Popover,
    Tooltip,
} from "@material-tailwind/react";

// @icons
import { Search, Sort, Car, Refresh } from "iconoir-react";

import { DialogAddVehicle } from "./DialgoAddVehicle";
import {
    Vehicle,
    listVehiclesRequest,
    listVehiclesSuccess,
    setVehicleRequest,
    updateVehicleRequest,
} from "../../../../redux/vehicles/actions";
import { getMoneyFormat, getVehicleStatusColor } from "../../../../utils/formatViews";
import AppImages from "../../../../assets/AppImages";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux";
import {
    DashboardVehicleInventoryFilterDialog,
    FilterOptionsProps,
} from "../../../../components/DashboardVehicleInventoryFilterDialog";

const TABLE_HEAD = [
    { n: "Make", className: "w-48" },
    { n: "Model", className: "" },
    { n: "Year", className: "" },
    { n: "Status", className: "w-20" },
    { n: "Days Owned", className: "w-28" },
    { n: "Title Type", className: "w-28" },
    { n: "Expenses", className: "w-28" },
    { n: "Cost", className: "w-28" },
    { n: "Profit", className: "w-28" },
    { n: "Location", className: "w-28" },
    { n: "VIN", className: "w-28" },
];

export function DashboardVehicleInventoryTable() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { vehicles } = useSelector((state: RootState) => state.vehicles);
    const [filterVehicles, setFilterVehicles] = React.useState<Vehicle[]>([]);
    const [openFilterDialog, setOpenFilterDialog] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedVehicles, setSelectedVehicles] = React.useState<number[]>([]);
    const [search, setSearch] = React.useState<string>("");
    const { auth } = useSelector((state: RootState) => state.auth);
    const [filterOptions, setFilterOptions] = React.useState<FilterOptionsProps>({
        year_range: [null, null],
        cost_range: [0, 100000],
        locations: [],
        statuses: [],
    });

    const handleCheckboxChange = (id: number) => {
        setSelectedVehicles((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const handleRefresh = () => {
        let excludes = "";

        if (!["SuperUser", "Admin", "Parts Analyst"].includes(auth?.user?.role?.title || "")) {
            excludes = "status__in:ready_for_sale|awaiting_payment|reserved|sold";
        }

        dispatch(
            listVehiclesRequest({
                filters: "is_purchased:True",
                vehicle_fields: [
                    'id',
                    'ymmt',
                    'profit',
                    'estimated_profit',
                    'estimated_sale_price',
                    'total_expenses',
                    'title_code',
                    'status',
                    'store',
                    'display_status',
                    'thumbnail',
                    'make',
                    'model',
                    'year',
                    'trim',
                    'vin',
                    'days_owned',
                ].join(','),
                excludes: excludes,
                save_as: "vehicles",
            })
        );
    };

    React.useEffect(() => {
        handleRefresh();
    }, [dispatch]);

    React.useEffect(() => {
        const handleFilterApply = () => {
            let result = [...vehicles];

            if (filterOptions.year_range[0]) {
                result = result.filter((el) => (!!el?.year && (el.year >= (filterOptions.year_range[0] || 0))));
            }

            if (filterOptions.year_range[1]) {
                result = result.filter((el) => (!!el?.year && (el.year <= (filterOptions.year_range[1] || 3000))));
            }

            result = result.filter(
                (el) =>
                    ((el.purchase_price || 0) + (el?.total_expenses || 0)) >= filterOptions.cost_range[0] &&
                    ((el.purchase_price || 0) + (el?.total_expenses || 0)) <= filterOptions.cost_range[1]
            );

            if (filterOptions.locations.length > 0) {
                result = result.filter((el) =>
                    filterOptions.locations.includes(el.store?.location || "")
                );
            }

            if (filterOptions.statuses.length > 0) {
                result = result.filter((el) => filterOptions.statuses.includes(el.status || ""));
            }

            if (search) {
                result = result.filter((el) => {
                    const searchString = `${el?.make} ${el?.model} ${el?.year} ${el?.trim} ${el?.vin} ${el?.store?.location} ${el?.title_code} ${el?.status} ${el?.days_owned} ${el?.purchase_price} ${el?.total_expenses} ${el?.profit}`;
                    return searchString.toLowerCase().includes(search.toLowerCase());
                });
            }

            setFilterVehicles(result);
        };

        handleFilterApply();
    }, [search, vehicles, filterOptions]);

    const getDaysOwned = (el: Vehicle) => {
        if ((el?.days_owned || 0) <= 0) {
            return "N/A";
        } else {
            return `${el?.days_owned || 0} Days`;
        }
    };

    return (
        <React.Fragment>
            <Card>
                <Card.Header className="w-full m-0 items-start rounded-none flex flex-wrap gap-4 justify-between p-4">
                    <div>
                        <Typography type="lead" color="default" className="font-semibold mb-1">
                            Vehicle Inventory
                        </Typography>
                        <Typography type="small" className="text-foreground block">
                            View all the orders from the previous year
                        </Typography>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center w-full shrink-0 gap-3 md:w-max">
                        <Input
                            placeholder="Search here..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full sm:w-72"
                        >
                            <Input.Icon>
                                <Search className="w-full h-full" />
                            </Input.Icon>
                        </Input>
                        <div className="flex gap-3 sm:w-max w-full">
                            <Popover open={openFilterDialog} onOpenChange={setOpenFilterDialog}>
                                <Popover.Trigger>
                                    <Button
                                        variant="outline"
                                        color="secondary"
                                        className="flex items-center gap-2 sm:w-auto w-full"
                                        onClick={() => setOpenFilterDialog(true)}
                                    >
                                        Filter
                                        <Sort className="w-4 h-4" />
                                    </Button>
                                </Popover.Trigger>
                                <Popover.Content className="max-w-sm">
                                    <div className="p-3 max-w-[300px]">
                                        <DashboardVehicleInventoryFilterDialog
                                            filterOptions={filterOptions}
                                            setFilterOptions={setFilterOptions}
                                            onApply={() => setOpenFilterDialog(false)}
                                            onCancel={() => setOpenFilterDialog(false)}
                                        />
                                    </div>
                                </Popover.Content>
                            </Popover>
                            {/* <Button
                            variant="outline"
                            color="secondary"
                            className="flex items-center gap-2 sm:w-auto w-full"
                        >
                            Export
                            <Download className="w-4 h-4" />
                        </Button> */}
                            <Button
                                variant="outline"
                                color="secondary"
                                className="flex items-center gap-2 sm:w-auto w-full"
                                onClick={() => {
                                    handleRefresh();
                                }}
                            >
                                <Refresh className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                color="error"
                                className="flex items-center gap-2 sm:w-auto w-full"
                                onClick={() => {
                                    if (selectedVehicles.length == 0) {
                                        return;
                                    }

                                    withReactContent(Swal)
                                        .fire({
                                            title: "Are you sure?",
                                            text: `You are about to remove ${selectedVehicles.length} vehicles`,
                                            icon: "warning",
                                            showCancelButton: true,
                                            confirmButtonColor: "#EF4444",
                                            confirmButtonText: "Remove",
                                        })
                                        .then((result: any) => {
                                            if (result.isConfirmed) {
                                                selectedVehicles
                                                    .map((id) => ({
                                                        id: id,
                                                        is_removed: true,
                                                    }))
                                                    .forEach((el) => {
                                                        dispatch(
                                                            updateVehicleRequest({
                                                                vehicle: el,
                                                                update_fields: ["id", "is_removed"],
                                                            })
                                                        );
                                                    });
                                                dispatch(
                                                    listVehiclesSuccess({
                                                        vehicles: vehicles.filter(
                                                            (t) =>
                                                                !selectedVehicles.includes(
                                                                    t.id || 0
                                                                )
                                                        ),
                                                        save_as: "vehicles",
                                                    })
                                                );
                                                setSelectedVehicles([]);
                                            }
                                        })
                                        .catch((error: any) => {
                                            console.log("error", error);
                                        });
                                }}
                            >
                                Remove
                                <AppImages.iconTrash className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                color="primary"
                                className="flex items-center gap-2 sm:w-auto w-full text-nowrap"
                                onClick={() => setIsOpen(true)}
                            >
                                Add Vehicle
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="mt-4 p-0 rounded-none max-h-[calc(100vh-240px)] overflow-y-auto scrollbar-hide">
                    <table className="w-full min-w-max table-auto">
                        <thead>
                            <tr>
                                {TABLE_HEAD.map((el, idx: number) => (
                                    <th
                                        key={idx}
                                        className={`sticky top-0 bg-white z-10 border-b text-start border-surface p-4 ${el.className}`}
                                    >
                                        {idx == 0 ? (
                                            <div className="flex items-center gap-5">
                                                <Checkbox
                                                    id="checkbox_all"
                                                    key={`${Math.random()}`}
                                                    checked={
                                                        selectedVehicles.length ==
                                                        filterVehicles.length
                                                    }
                                                    onChange={() => {
                                                        if (
                                                            selectedVehicles.length ==
                                                            filterVehicles.length
                                                        ) {
                                                            setSelectedVehicles([]);
                                                        } else {
                                                            setSelectedVehicles(
                                                                filterVehicles.map(
                                                                    (el) => el.id || 0
                                                                )
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <Checkbox.Indicator />
                                                </Checkbox>
                                                <Typography
                                                    type="small"
                                                    color="default"
                                                    className="block font-medium"
                                                >
                                                    {el.n}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="block font-medium"
                                            >
                                                {el.n}
                                            </Typography>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filterVehicles?.map((el: Vehicle) => (
                                <tr
                                    key={el.id}
                                    className="cursor-pointer"
                                    onClick={(e) => {
                                        if (
                                            (e.target as HTMLElement).tagName === "INPUT" ||
                                            (e.target as HTMLElement).tagName === "IMG" ||
                                            (e.target as HTMLElement).tagName === "LABEL"
                                        ) {
                                            return;
                                        }

                                        dispatch(setVehicleRequest(el));
                                        navigate("/dashboard/vehicles/inventory/" + el.vin);
                                    }}
                                >
                                    <td className="px-4 border-b border-surface">
                                        <div className="flex items-center gap-5">
                                            <Checkbox
                                                id={`checkbox_${el.id}`}
                                                key={`${el.id}_${Math.random()}`}
                                                checked={selectedVehicles.includes(el.id || 0)}
                                                onChange={() => handleCheckboxChange(el.id || 0)}
                                            >
                                                <Checkbox.Indicator />
                                            </Checkbox>
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="py-4 overflow-hidden flex items-center text-ellipsis w-48 !text-nowrap text-foreground font-medium"
                                            >
                                                {el.thumbnail?.full_url ? (
                                                    <Avatar
                                                        src={el.thumbnail.full_url}
                                                        alt={el.ymmt || ""}
                                                        shape="rounded"
                                                        className="mr-5"
                                                    />
                                                ) : (
                                                    <div className="bg-surface rounded-full p-2 mr-5">
                                                        <Car className="w-6 h-6" />
                                                    </div>
                                                )}
                                                {el.make}
                                            </Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el.model}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el.year}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        {el.status && (
                                            <Chip
                                                size="sm"
                                                isPill={false}
                                                className="w-max"
                                                color={getVehicleStatusColor(el.status)}
                                            >
                                                <Chip.Label className="capitalize">
                                                    {el.display_status}
                                                </Chip.Label>
                                            </Chip>
                                        )}
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {getDaysOwned(el)}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el.title_code}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {getMoneyFormat(el?.total_expenses)}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {getMoneyFormat(el?.purchase_price)}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el?.sale_price
                                                ? getMoneyFormat(el?.profit || 0)
                                                : el?.estimated_sale_price
                                                ? getMoneyFormat(el?.estimated_profit || 0)
                                                : "N/A"}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el?.store?.location || "N/A"}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Tooltip>
                                            <Tooltip.Trigger>
                                                <Typography
                                                    type="small"
                                                    className="block text-foreground w-20 !text-nowrap"
                                                >
                                                    ...{el?.vin?.slice(-8)}
                                                </Typography>
                                            </Tooltip.Trigger>
                                            <Tooltip.Content>
                                                {el.vin}
                                                <Tooltip.Arrow />
                                            </Tooltip.Content>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card.Body>
            </Card>

            {isOpen && (
                <DialogAddVehicle
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    onSubmit={(vehicle) => {
                        setIsOpen(false);
                        dispatch(
                            updateVehicleRequest({
                                vehicle: {
                                    ...vehicle,
                                    purchase_date: format(
                                        vehicle?.purchase_date || new Date(),
                                        "yyyy-MM-dd"
                                    ),
                                },
                                update_fields: Object.keys(vehicle),
                                cb: (vehicle: Vehicle) => {
                                    navigate(
                                        "/dashboard/vehicles/inventory/" + (vehicle?.vin || "")
                                    );
                                },
                            })
                        );
                    }}
                />
            )}
        </React.Fragment>
    );
}
